import { useState, useEffect } from "react";
/**
 * React hook for fetching data from an API endpoint.
 *
 * @template T The expected response data type.
 * @param {string} url - The API endpoint to fetch data from.
 * @param {RequestInit} [options] - Optional fetch options (method, headers, body, etc).
 * @param {any[]} [deps=[]] - Optional array of dependencies that will trigger a refetch when changed (in addition to the URL).
 * @param {string} method - HTTP method (GET, POST, DELETE). Defaults to 'GET'.
 * @returns {{ data: T | null, loading: boolean, error: boolean }}
 *   - data: The response data (or null if not loaded or on error)
 *   - loading: True while the request is in progress
 *   - error: True if the request failed
 *
 * The request is automatically aborted if the component unmounts or the URL/deps change.
 */

export function useApi<T>(
    url: string,
    options?: RequestInit,
    deps: any[] = [],
    method: 'GET' | 'POST' | 'DELETE' = 'GET'   
): { data: T | null, loading: boolean, error: boolean } {
    // Use 'any' to avoid issues with class types as generics in useState
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        setError(false);

        const controller = new AbortController();

        fetch(url, { ...options, method, signal: controller.signal })
            .then(async res => {
                if (!res.ok) throw new Error("Network error");
                const text = await res.text();
                if (!text) return null;
                let json: any;
                try {
                    json = JSON.parse(text);
                } catch (e) {
                    // If parsing fails, return null
                    return null;
                }
                // If T is CphResult<boolean>, ensure the shape matches
                if (
                    typeof json === 'object' &&
                    json !== null &&
                    ('Succeeded' in json || 'success' in json)
                ) {
                    return json as T;
                }
                return json as T;
            })
            .then(json => {
                if (isMounted) setData(json);
            })
            .catch((err) => {
                if (err instanceof Error && err.name === 'AbortError') {
                    return;
                }
                console.log('useApi error:', err);
                if (isMounted) setError(true);
            })
            .finally(() => {
                if (isMounted) setLoading(false);
            });

        return () => {
            isMounted = false;
            controller.abort();
        };
    // eslint-disable-next-line
    }, [url, ...deps]);

    // Cast data to T for return
    return { data: data as T | null, loading, error };
}